// Keyword extraction for reviewer search (PRD §4 Feature 5, §11).
//
// Approach: term-frequency scoring on unigrams, bigrams, and trigrams after
// stopword filtering and basic noun-phrase shape filtering.
//
// P2-8 — two refinements that materially improve reviewer-match quality:
//
//   1. Author-declared keywords (Editorial Manager cover "Keywords:" field)
//      are authoritative — the authors hand-picked the terms that best
//      describe their work. They are emitted FIRST with kind 'author' so the
//      reviewer search query (top-8) is anchored on the manuscript's own
//      keywords rather than whatever happens to be frequent in the prose.
//
//   2. IDF-ish demotion of generic scaffolding words. We are local-first and
//      ship no document-frequency corpus, so true TF-IDF is out; instead a
//      small curated list of ubiquitous academic/medical terms ("study",
//      "results", "patients", …) is multiplicatively down-weighted so
//      specific domain terms rise to the top-8. It only DEMOTES (never drops)
//      and only touches unigrams — multi-word phrases are inherently
//      specific. This shapes a search query, not a finding, so the failure
//      mode is at worst a slightly less relevant reviewer, never a false flag.

import stopwordsText from '../../../data/stopwords/english-medical.txt?raw';
import type { ManuscriptDocument } from '../pdf/types';
import type { KeywordResult } from './types';

const STOPWORDS: Set<string> = new Set(
  stopwordsText
    .split(/\r?\n/)
    .map((l) => l.trim().toLowerCase())
    .filter((l) => l.length > 0 && !l.startsWith('#')),
);

const TOKEN_RE = /[a-z][a-z0-9-]{2,}/g;

// Ubiquitous academic/medical scaffolding unigrams. These appear in virtually
// every manuscript and carry almost no reviewer-discriminating signal, so
// their term-frequency score is multiplicatively demoted (not removed — they
// can still surface, just below specific domain terms). Conservative on
// purpose: only words that are generic *across* medical subfields. Domain
// nouns ("hypertension", "tumour", "cohort", drug names) are deliberately
// excluded so they keep their full weight.
const GENERIC_TERMS: Set<string> = new Set([
  'study',
  'studies',
  'result',
  'results',
  'method',
  'methods',
  'methodology',
  'conclusion',
  'conclusions',
  'background',
  'objective',
  'objectives',
  'aim',
  'aims',
  'purpose',
  'finding',
  'findings',
  'analysis',
  'analyses',
  'data',
  'group',
  'groups',
  'patient',
  'patients',
  'participant',
  'participants',
  'subject',
  'subjects',
  'case',
  'cases',
  'outcome',
  'outcomes',
  'effect',
  'effects',
  'factor',
  'factors',
  'level',
  'levels',
  'rate',
  'rates',
  'value',
  'values',
  'number',
  'mean',
  'difference',
  'significant',
  'significance',
  'associated',
  'association',
  'compared',
  'comparison',
  'observed',
  'reported',
  'included',
  'using',
  'used',
  'based',
  'baseline',
  'follow',
  'total',
  'overall',
  'present',
  'current',
  'clinical',
  'medical',
  'health',
  'care',
  'treatment',
  'patient-reported',
]);

/** Multiplicative penalty applied to a generic-term unigram score. */
const GENERIC_DEMOTION = 0.35;

function isContentBigram(a: string, b: string): boolean {
  return !STOPWORDS.has(a) && !STOPWORDS.has(b);
}

interface ExtractOptions {
  /** Cap on results returned. Default 20. */
  limit?: number;
  /** Minimum unigram frequency to surface. Default 3. */
  minUnigramFreq?: number;
  /** Minimum n-gram frequency for bigrams/trigrams. Default 2. */
  minNgramFreq?: number;
}

export function extractKeywords(doc: ManuscriptDocument, opts: ExtractOptions = {}): KeywordResult[] {
  const limit = opts.limit ?? 20;
  const minUni = opts.minUnigramFreq ?? 3;
  const minN = opts.minNgramFreq ?? 2;

  // Build the token stream from title + abstract + body (excluding references).
  const sectionFilter = new Set(['title', 'abstract', 'introduction', 'methods', 'results', 'discussion']);
  let scopedText = '';
  if (doc.sections.length > 0) {
    for (const span of doc.sections) {
      if (!sectionFilter.has(span.section)) continue;
      scopedText += ' ' + doc.rawText.slice(span.start, span.end);
    }
    if (doc.title) scopedText = doc.title + ' ' + scopedText;
  } else {
    scopedText = doc.rawText;
  }

  // Tokenize with positions: we need adjacency for n-grams. Use the raw token
  // stream (including stopwords) so bigrams reflect actual adjacency, then
  // filter at scoring time.
  const rawTokens = scopedText.toLowerCase().match(TOKEN_RE) ?? [];

  const uni = new Map<string, number>();
  const bi = new Map<string, number>();
  const tri = new Map<string, number>();

  for (let i = 0; i < rawTokens.length; i++) {
    const t = rawTokens[i];
    if (!STOPWORDS.has(t)) {
      uni.set(t, (uni.get(t) ?? 0) + 1);
    }
    if (i + 1 < rawTokens.length) {
      const a = rawTokens[i];
      const b = rawTokens[i + 1];
      if (isContentBigram(a, b)) {
        const k = `${a} ${b}`;
        bi.set(k, (bi.get(k) ?? 0) + 1);
      }
    }
    if (i + 2 < rawTokens.length) {
      const a = rawTokens[i];
      const b = rawTokens[i + 1];
      const c = rawTokens[i + 2];
      // For trigrams, require endpoints to be content words; the middle may be a
      // stopword like "of" or "in" (common in medical noun phrases).
      if (!STOPWORDS.has(a) && !STOPWORDS.has(c)) {
        const k = `${a} ${b} ${c}`;
        tri.set(k, (tri.get(k) ?? 0) + 1);
      }
    }
  }

  const body: KeywordResult[] = [];
  for (const [term, freq] of uni) {
    if (freq < minUni) continue;
    // IDF-ish: demote ubiquitous scaffolding words so specific domain terms
    // win the top-8 slots that actually feed the reviewer query.
    const score = GENERIC_TERMS.has(term) ? freq * GENERIC_DEMOTION : freq;
    body.push({ term, score, kind: 'unigram' });
  }
  for (const [term, freq] of bi) {
    if (freq < minN) continue;
    // Boost bigrams: they're more specific than unigrams.
    body.push({ term, score: freq * 1.5, kind: 'bigram' });
  }
  for (const [term, freq] of tri) {
    if (freq < minN) continue;
    body.push({ term, score: freq * 2, kind: 'trigram' });
  }

  body.sort((a, b) => b.score - a.score);

  // Author-declared EM cover keywords are authoritative: emit them FIRST (in
  // the authors' own order), deduped against each other and against any
  // body-derived term so the same concept is not double-counted. They feed
  // the top of the reviewer-search query regardless of prose frequency.
  const authorKeywords = doc.emCover?.keywords ?? [];
  const seen = new Set<string>();
  const authored: KeywordResult[] = [];
  for (const raw of authorKeywords) {
    const term = raw.replace(/\s+/g, ' ').trim();
    if (term.length < 2) continue;
    const key = term.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    authored.push({ term, score: 100, kind: 'author' });
  }
  const deduped = body.filter((b) => !seen.has(b.term.toLowerCase()));

  return [...authored, ...deduped].slice(0, limit);
}
